import React from 'react';
import styled from 'styled-components';

import { isPermitted } from 'util/PermissionsMixin';
import useCurrentUser from 'hooks/useCurrentUser';
import { Link } from 'components/common/router';
import { RestrictedAccessTooltip } from 'components/common';
import { defaultCompare } from 'logic/DefaultCompare';
import useShowRouteFromGRN from 'routing/hooks/useShowRouteFromGRN';
import type { CollectionEntity, CollectionOwner } from 'collections/types';
import { getValuesFromGRN } from 'logic/permissions/GRN';

type Props = Pick<CollectionEntity, 'owners'>;

const OwnerList = styled.div`
  display: flex;
`;

const OwnerTitleWrapper = styled.span`
  display: flex;
  align-items: center;
`;

const TitleWithLink = ({ title, entityGrn }: { title: string; entityGrn: string }) => {
  const entityRoute = useShowRouteFromGRN(entityGrn);

  return <Link to={entityRoute}>{title}</Link>;
};

const assertUnreachable = (type: string): never => {
  throw new Error(`Owner of entity has not supported type: ${type}`);
};

type OwnerTitleProps = {
  owner: CollectionOwner;
};

const OwnerTitle = ({ owner: { type, id: grn, title } }: OwnerTitleProps) => {
  const currentUser = useCurrentUser();
  const { id: ownerId } = getValuesFromGRN(grn);

  switch (type) {
    case 'user':
      if (!isPermitted(currentUser.permissions, `users:edit:${ownerId}`))
        return (
          <OwnerTitleWrapper>
            {title} <RestrictedAccessTooltip entityName={type} capabilityName="view" />
          </OwnerTitleWrapper>
        );

      return <TitleWithLink entityGrn={grn} title={title} />;
    case 'team':
      if (!isPermitted(currentUser.permissions, `team:edit:${ownerId}`))
        return (
          <OwnerTitleWrapper>
            {title} <RestrictedAccessTooltip entityName={type} capabilityName="view" />
          </OwnerTitleWrapper>
        );

      return <TitleWithLink entityGrn={grn} title={title} />;
    case 'global':
      return <span>Everyone</span>;
    default:
      return assertUnreachable(type);
  }
};

const OwnersCell = ({ owners }: Props) => {
  const sortedOwners = owners.sort((o1, o2) => defaultCompare(o1.type, o2.type) || defaultCompare(o1.title, o2.title));

  return (
    <OwnerList>
      {sortedOwners.map((owner, index) => {
        const isLast = index >= owners.length - 1;

        return (
          <React.Fragment key={owner.id}>
            <OwnerTitle owner={owner} />
            {!isLast && <>, &nbsp;</>}
          </React.Fragment>
        );
      })}{' '}
    </OwnerList>
  );
};
export default OwnersCell;
